import React, { useEffect, useState } from 'react';

function SaveSearchModal({ isOpen, onClose, onSaved, currentFilters, userId }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a name for your search');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      userId,
      name: name.trim(),
      zip: currentFilters.zip || null,
      makeName: currentFilters.makeName || null,
      model: currentFilters.model || null,
      trim: currentFilters.trim || null,
      minYear: toNum(currentFilters.minYear),
      maxYear: toNum(currentFilters.maxYear),
      minPrice: toNum(currentFilters.minPrice),
      maxPrice: toNum(currentFilters.maxPrice),
      maxDistance: toNum(currentFilters.maxDistance),
      maxMileage: toNum(currentFilters.maxMileage),
      sort: currentFilters.sort || null,
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/savedsearches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Failed to save search');
      }

      window.gtag?.('event', 'save_search', {
        event_category: 'engagement',
      });

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Error saving search.');
    } finally {
      setSaving(false);
    }
  };

  const summaryParts = [
    currentFilters.zip && `Zip: ${currentFilters.zip}`,
    currentFilters.makeName && `Make: ${currentFilters.makeName}`,
    currentFilters.model && `Model: ${currentFilters.model}`,
    currentFilters.trim && `Trim: ${currentFilters.trim}`,
    currentFilters.minYear && `Year >= ${currentFilters.minYear}`,
    currentFilters.maxYear && `Year <= ${currentFilters.maxYear}`,
    currentFilters.minPrice && `Price >= $${Number(currentFilters.minPrice).toLocaleString()}`,
    currentFilters.maxPrice && `Price <= $${Number(currentFilters.maxPrice).toLocaleString()}`,
    currentFilters.maxDistance && `Within ${currentFilters.maxDistance} mi`,
    currentFilters.maxMileage && `Max ${Number(currentFilters.maxMileage).toLocaleString()} mi`,
    currentFilters.sort && `Sort: ${currentFilters.sort}`,
  ].filter(Boolean);

  return (
    <div
      onClick={onClose}
      className="ui-modal-overlay items-start sm:items-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ui-modal max-w-lg"
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold text-slate-950">Save this search</h3>
            <p className="mt-1 text-sm text-slate-600">
              Name the search so you can revisit it later.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="btn-icon bg-slate-950 text-white hover:bg-slate-800 hover:text-white"
            type="button"
          >
            &times;
          </button>
        </div>

        <label className="ui-field-label">Name</label>
        <input
          type="text"
          placeholder="e.g., Miami BMW under $30k"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-3"
          onKeyDown={(e) => e.key === 'Enter' && !saving && handleSave()}
        />

        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs leading-6 text-slate-600">
          {summaryParts.join(' | ')}
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button onClick={onClose} disabled={saving} className="btn-secondary" type="button">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
            type="button"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SaveSearchModal;
